import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Upload, Image as ImageIcon, ArrowUp, ArrowDown } from 'lucide-react';
import { getProducts, saveProduct, deleteProduct, getCategories, getAllSubcategories } from '../../data/storeData';

const inputClass = "w-full px-4 py-2.5 bg-[#fbfbfd] border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-transparent transition-all placeholder:text-gray-400";
const labelClass = "block text-[13px] font-semibold text-gray-700 mb-1.5";

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    id: Date.now(),
    categoryId: '',
    subcategoryId: '',
    name: '',
    specs: '',
    basePrice: 0,
    salePrice: null,
    variants: [],
    tag: '',
    image: null,
    colorClass: 'bg-gray-500',
    featured: false,
    order: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [prods, cats, subs] = await Promise.all([
        getProducts(),
        getCategories(),
        getAllSubcategories()
      ]);

      // Sort by order
      const sortedProds = (prods || []).sort((a, b) => (a.order || 0) - (b.order || 0));

      setProducts(sortedProds);
      setCategories(cats || []);
      setSubcategories(subs || []);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData(prev => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox'
        ? checked
        : name === 'order' || name === 'basePrice' || name === 'salePrice'
          ? parseFloat(value) || 0
          : value
    }));
  };

  const addVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, { storage: '', color: '', hex: '#000000', price: 0 }]
    }));
  };

  const updateVariant = (index, field, value) => {
    const newVariants = [...formData.variants];
    newVariants[index][field] = field === 'price' ? parseFloat(value) || 0 : value;
    setFormData(prev => ({ ...prev, variants: newVariants }));
  };

  const removeVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.categoryId || !formData.subcategoryId) {
      alert('Please fill in required fields');
      return;
    }

    try {
      const productData = {
        ...formData,
        basePrice: parseFloat(formData.basePrice) || 0,
        salePrice: formData.salePrice ? parseFloat(formData.salePrice) : null,
        order: parseInt(formData.order) || 0,
        id: editingProduct ? editingProduct.id : Date.now()
      };

      await saveProduct(productData);
      await loadData();
      resetForm();
      alert(editingProduct ? 'Product updated!' : 'Product added!');
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product.');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      ...product,
      order: product.order || 0
    });
    setImagePreview(product.image);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product?')) return;

    try {
      await deleteProduct(productId);
      await loadData();
      alert('Product deleted!');
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error deleting product');
    }
  };

  const moveProduct = async (index, direction) => {
    const newProducts = [...products];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newProducts.length) return;

    const tempOrder = newProducts[index].order || index;
    newProducts[index] = { ...newProducts[index], order: newProducts[targetIndex].order || targetIndex };
    newProducts[targetIndex] = { ...newProducts[targetIndex], order: tempOrder };

    try {
      await saveProduct(newProducts[index]);
      await saveProduct(newProducts[targetIndex]);
      await loadData();
    } catch (error) {
      console.error('Error moving product:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      id: Date.now(),
      categoryId: '',
      subcategoryId: '',
      name: '',
      specs: '',
      basePrice: 0,
      salePrice: null,
      variants: [],
      tag: '',
      image: null,
      colorClass: 'bg-gray-500',
      featured: false,
      order: 0
    });
    setImagePreview(null);
    setEditingProduct(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-12 h-12 border-[3px] border-orange-500/20 border-t-orange-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add / Edit Form */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] p-6 md:p-8">
        <div className="mb-7">
          <div className="w-10 h-1 bg-orange-500 rounded-full mb-3" />
          <h2 className="text-2xl font-bold text-[#1d1d1f] tracking-tight">
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </h2>
          {editingProduct && (
            <p className="text-sm text-gray-500 mt-1">Editing: {editingProduct.name}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Product Name *</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required
                className={inputClass} placeholder="iPhone 15" />
            </div>

            <div>
              <label className={labelClass}>Specifications</label>
              <input type="text" name="specs" value={formData.specs} onChange={handleChange}
                className={inputClass} placeholder="Dynamic Island" />
            </div>

            <div>
              <label className={labelClass}>Category *</label>
              <select name="categoryId" value={formData.categoryId} onChange={handleChange} required
                className={inputClass}>
                <option value="">Select Category</option>
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
            </div>

            <div>
              <label className={labelClass}>Subcategory *</label>
              <select name="subcategoryId" value={formData.subcategoryId} onChange={handleChange} required
                className={inputClass}>
                <option value="">Select Subcategory</option>
                {subcategories.filter(sub => sub.categoryId === formData.categoryId).map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Base Price *</label>
              <input type="number" name="basePrice" value={formData.basePrice} onChange={handleChange} required min="0" step="0.01"
                className={inputClass} placeholder="999" />
            </div>

            <div>
              <label className={labelClass}>Sale Price</label>
              <input type="number" name="salePrice" value={formData.salePrice || ''} onChange={handleChange} min="0" step="0.01"
                className={inputClass} placeholder="799" />
            </div>

            <div>
              <label className={labelClass}>Tag</label>
              <input type="text" name="tag" value={formData.tag} onChange={handleChange}
                className={inputClass} placeholder="New, Sale" />
            </div>

            <div>
              <label className={labelClass}>Color Class</label>
              <input type="text" name="colorClass" value={formData.colorClass} onChange={handleChange}
                className={inputClass} placeholder="bg-gray-500" />
            </div>

            <div>
              <label className={labelClass}>Display Order</label>
              <input type="number" name="order" value={formData.order} onChange={handleChange} min="0" max="1000"
                className={inputClass} placeholder="0" />
              <p className="text-xs text-gray-400 mt-1.5">Lower = First (0, 1, 2, 3...)</p>
            </div>
          </div>

          <div>
            <label className={labelClass}>Product Image</label>
            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1d1d1f] text-white rounded-full hover:bg-black cursor-pointer text-sm font-semibold transition-all active:scale-95 shadow-sm">
                <Upload className="w-4 h-4" strokeWidth={1.75} />
                Upload Image
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>
              {imagePreview && (
                <div className="relative">
                  <img src={imagePreview} alt="Preview" className="w-16 h-16 object-cover rounded-xl border border-gray-200" />
                  <button type="button" onClick={() => { setImagePreview(null); setFormData(prev => ({ ...prev, image: null })); }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-md transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <label htmlFor="featured" className="inline-flex items-center gap-2.5 cursor-pointer select-none">
            <input type="checkbox" name="featured" id="featured" checked={formData.featured} onChange={handleChange}
              className="w-4 h-4 accent-orange-500 rounded" />
            <span className="text-sm font-semibold text-gray-700">Featured Product</span>
          </label>

          {/* Variants */}
          <div className="border-t border-gray-100 pt-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-[#1d1d1f] tracking-tight">Variants</h3>
                <p className="text-sm text-gray-400">Each row = storage + color + price</p>
              </div>
              <button type="button" onClick={addVariant}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 text-sm font-semibold transition-all active:scale-95 shadow-[0_8px_20px_-6px_rgba(249,115,22,0.4)]">
                <Plus className="w-4 h-4" strokeWidth={2.5} />
                Add Variant
              </button>
            </div>

            <div className="space-y-3">
              {formData.variants.map((variant, index) => (
                <div key={index} className="bg-[#fbfbfd] border border-gray-100 p-4 rounded-2xl flex items-center gap-3">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input type="text" value={variant.storage} onChange={(e) => updateVariant(index, 'storage', e.target.value)}
                      placeholder="128GB" className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/60" />
                    <input type="text" value={variant.color} onChange={(e) => updateVariant(index, 'color', e.target.value)}
                      placeholder="Blue" className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/60" />
                    <div className="flex items-center gap-2">
                      <input type="color" value={variant.hex} onChange={(e) => updateVariant(index, 'hex', e.target.value)}
                        className="w-11 h-10 border border-gray-200 rounded-xl cursor-pointer bg-white p-1" />
                      <input type="text" value={variant.hex} onChange={(e) => updateVariant(index, 'hex', e.target.value)}
                        placeholder="#000000" className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/60" />
                    </div>
                    <input type="number" value={variant.price} onChange={(e) => updateVariant(index, 'price', e.target.value)}
                      placeholder="Price" min="0" step="0.01" className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/60" />
                  </div>
                  <button type="button" onClick={() => removeVariant(index)}
                    className="p-2.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors">
                    <Trash2 className="w-5 h-5" strokeWidth={1.75} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit"
              className="inline-flex items-center gap-2 px-7 py-3 bg-[#1d1d1f] text-white rounded-full hover:bg-black font-semibold transition-all active:scale-95 shadow-[0_8px_20px_-6px_rgba(0,0,0,0.35)]">
              <Save className="w-4 h-4" strokeWidth={1.75} />
              {editingProduct ? 'Update' : 'Add'} Product
            </button>
            {editingProduct && (
              <button type="button" onClick={resetForm}
                className="px-7 py-3 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 font-semibold transition-all active:scale-95">
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Products List */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.06)] p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="w-10 h-1 bg-orange-500 rounded-full mb-3" />
            <h2 className="text-2xl font-bold text-[#1d1d1f] tracking-tight">All Products</h2>
          </div>
          <span className="px-3.5 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm font-bold">
            {products.length}
          </span>
        </div>

        <div className="overflow-x-auto -mx-2 px-2">
          <table className="w-full">
            <thead>
              <tr className="text-left text-[11px] font-bold tracking-wider uppercase text-gray-400 border-b border-gray-100">
                <th className="py-3 px-4">Order</th>
                <th className="py-3 px-4">Image</th>
                <th className="py-3 px-4">Name</th>
                <th className="py-3 px-4">Category</th>
                <th className="py-3 px-4">Price</th>
                <th className="py-3 px-4">Variants</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => (
                <tr key={product.id} className="border-b border-gray-50 hover:bg-[#fbfbfd] transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex flex-col items-center gap-1">
                      <button onClick={() => moveProduct(index, 'up')} disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-[#1d1d1f] hover:bg-gray-100 rounded-md disabled:opacity-20 transition-colors" title="Move up">
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-md">{product.order || 0}</span>
                      <button onClick={() => moveProduct(index, 'down')} disabled={index === products.length - 1}
                        className="p-1 text-gray-400 hover:text-[#1d1d1f] hover:bg-gray-100 rounded-md disabled:opacity-20 transition-colors" title="Move down">
                        <ArrowDown className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {product.image ? (
                      <img src={product.image} alt={product.name} className="w-12 h-12 object-cover rounded-xl border border-gray-100" />
                    ) : (
                      <div className={`w-12 h-12 ${product.colorClass} rounded-xl flex items-center justify-center`}>
                        <ImageIcon className="w-5 h-5 text-white/50" />
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="font-semibold text-[#1d1d1f]">{product.name}</div>
                    <div className="text-sm text-gray-400">{product.specs}</div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-sm font-medium text-gray-700">{categories.find(c => c.id === product.categoryId)?.name || 'N/A'}</div>
                    <div className="text-xs text-gray-400">{subcategories.find(s => s.id === product.subcategoryId)?.name || 'N/A'}</div>
                  </td>
                  <td className="py-3 px-4">
                    {product.salePrice ? (
                      <div>
                        <div className="font-bold text-orange-500 tabular-nums">${product.salePrice}</div>
                        <div className="text-sm text-gray-300 line-through tabular-nums">${product.basePrice}</div>
                      </div>
                    ) : (
                      <div className="font-bold text-[#1d1d1f] tabular-nums">${product.basePrice}</div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">
                      {product.variants?.length || 0}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => handleEdit(product)}
                        className="p-2.5 text-gray-400 hover:text-[#1d1d1f] hover:bg-gray-100 rounded-xl transition-colors" title="Edit">
                        <Edit className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                      <button onClick={() => handleDelete(product.id)}
                        className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors" title="Delete">
                        <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProductsTab;
